import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import GroupIcon from '@mui/icons-material/Group';
import LinkIcon from '@mui/icons-material/Link';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { usePlatformAnalytics } from '../../hooks/useAnalytics';

// ─── Stat Card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  loading?: boolean;
}

function StatCard({ icon, label, value, subtitle, loading }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 13 } }}>
            {label}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {value}
          </Typography>
        )}
        {subtitle && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: 11, sm: 12 } }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatSourceLabel(source: string): string {
  return source
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(d);
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function PlatformAnalyticsPage() {
  const { data, isLoading, error } = usePlatformAnalytics();

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load platform analytics. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Platform Analytics
      </Typography>

      {/* Overall Stats — 2x3 grid on mobile, single row on desktop */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCard
            icon={<EventIcon fontSize="small" />}
            label="Total Events"
            value={data?.events.total ?? 0}
            subtitle={`${data?.events.active ?? 0} active, ${data?.events.completed ?? 0} completed`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCard
            icon={<MailOutlineIcon fontSize="small" />}
            label="Delivery Rate"
            value={isLoading ? '' : `${data?.emails.deliveryRate ?? 0}%`}
            subtitle={`${data?.emails.totalSent ?? 0} emails sent`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCard
            icon={<MailOutlineIcon fontSize="small" />}
            label="Open Rate"
            value={isLoading ? '' : `${data?.emails.openRate ?? 0}%`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCard
            icon={<MailOutlineIcon fontSize="small" />}
            label="Bounce Rate"
            value={isLoading ? '' : `${data?.emails.bounceRate ?? 0}%`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCard
            icon={<GroupIcon fontSize="small" />}
            label="Conversion Rate"
            value={isLoading ? '' : `${data?.rsvps.averageConversionRate ?? 0}%`}
            subtitle={`${data?.rsvps.totalResponses ?? 0} total responses`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCard
            icon={<LinkIcon fontSize="small" />}
            label="Referral Clicks"
            value={data?.referrals.totalClicks ?? 0}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Referral Tracking */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Referral Clicks by Source
            </Typography>
          </Box>
          {isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="text" width="70%" sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : data?.referrals.bySource && Object.keys(data.referrals.bySource).length > 0 ? (
            <List disablePadding>
              {Object.entries(data.referrals.bySource)
                .sort(([, a], [, b]) => b - a)
                .map(([source, count]) => (
                  <ListItem
                    key={source}
                    disableGutters
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: 13, sm: 14 } }}>
                            {formatSourceLabel(source)}
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: 13, sm: 14 } }}>
                            {count}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No referral clicks recorded yet.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Growth — New hosts and events */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrendingUpIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Growth (Last 30 Days)
            </Typography>
          </Box>
          {isLoading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="text" width="90%" sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : data?.growth && data.growth.length > 0 ? (
            <List disablePadding>
              {data.growth.map((entry) => (
                <ListItem
                  key={entry.date}
                  disableGutters
                  sx={{
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: 12, sm: 13 } }}>
                          {formatDate(entry.date)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {entry.newHosts > 0 && (
                            <Typography variant="body2" color="primary.main" sx={{ fontSize: { xs: 12, sm: 13 } }}>
                              +{entry.newHosts} host{entry.newHosts !== 1 ? 's' : ''}
                            </Typography>
                          )}
                          {entry.newEvents > 0 && (
                            <Typography variant="body2" color="secondary.main" sx={{ fontSize: { xs: 12, sm: 13 } }}>
                              +{entry.newEvents} event{entry.newEvents !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No growth data for this period.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
